import {
  Badge,
  Box,
  Flex,
  FlexItem,
  H1,
  Message,
  Panel,
  ProgressCircle,
  Tabs,
  Text,
} from '@bigcommerce/big-design';
import { lazy, Suspense, useMemo, useState } from 'react';

import { useAdminBadges } from '@/hooks/useAdminBadges';
import { useSession } from '@/hooks/useSession';
import { DashboardPage } from '@/pages/Dashboard';
import { LocationsPage } from '@/pages/Locations';

// Lazy load pages to reduce initial bundle size
// Organized by tab groups for maintainability

// Operations (Daily Use)
const ReportsPage = lazy(() => import('@/pages/Reports').then(m => ({ default: m.ReportsPage })));
const InventoryPage = lazy(() => import('@/pages/Inventory').then(m => ({ default: m.InventoryPage })));

// Store Setup
const StaffPage = lazy(() => import('@/pages/Staff').then(m => ({ default: m.StaffPage })));
const DevicesPage = lazy(() => import('@/pages/Devices').then(m => ({ default: m.DevicesPage })));

// Checkout Configuration
const PaymentsPage = lazy(() => import('@/pages/Payments').then(m => ({ default: m.PaymentsPage })));
const OrdersPage = lazy(() => import('@/pages/Orders').then(m => ({ default: m.OrdersPage })));
const ProductsPage = lazy(() => import('@/pages/Products').then(m => ({ default: m.ProductsPage })));
const TaxPage = lazy(() => import('@/pages/Tax').then(m => ({ default: m.TaxPage })));

// Marketing
const PromotionsPage = lazy(() => import('@/pages/Promotions').then(m => ({ default: m.PromotionsPage })));
const PriceListsPage = lazy(() => import('@/pages/PriceLists').then(m => ({ default: m.PriceListsPage })));
const GiftCardsPage = lazy(() => import('@/pages/GiftCards').then(m => ({ default: m.GiftCardsPage })));

// Settings
const ReceiptsPage = lazy(() => import('@/pages/Receipts').then(m => ({ default: m.ReceiptsPage })));
const SettingsPage = lazy(() => import('@/pages/General').then(m => ({ default: m.GeneralPage })));

// Business Tier
const AuditLogsPage = lazy(() => import('@/pages/AuditLogs').then(m => ({ default: m.AuditLogsPage })));

// Account
const BillingPage = lazy(() => import('@/pages/Billing').then(m => ({ default: m.BillingPage })));

function PageLoader() {
  return (
    <Box padding="large">
      <Flex justifyContent="center">
        <ProgressCircle size="medium" />
      </Flex>
    </Box>
  );
}

/**
 * Tab IDs for the admin panel navigation.
 * Tabs are organized into logical groups for better UX:
 *
 * OPERATIONS (Daily Use):     Dashboard, Reports
 * STORE SETUP:                Stores, Staff, Devices
 * CHECKOUT CONFIGURATION:     Payments, Orders (includes Exchanges), Products, Tax
 * MARKETING:                  Promotions
 * SETTINGS:                   Receipts, Settings
 */
type TabId =
  // Operations
  | 'dashboard' | 'reports' | 'inventory'
  // Store Setup
  | 'stores' | 'staff' | 'devices'
  // Checkout Configuration
  | 'payments' | 'orders' | 'products' | 'tax'
  // Marketing
  | 'promotions' | 'pricelists' | 'giftcards'
  // Settings
  | 'receipts' | 'settings'
  // Business Tier
  | 'auditlogs'
  // Account
  | 'billing';

// Base tab definitions (badges added dynamically)
const baseTabs = [
  // --- OPERATIONS (Daily Use) ---
  { id: 'dashboard' as const, title: '📊 Dashboard', badgeKey: null },
  { id: 'reports' as const, title: '📈 Reports', badgeKey: null },
  { id: 'inventory' as const, title: '📦 Inventory', badgeKey: null },

  // --- STORE SETUP ---
  { id: 'stores' as const, title: '🏪 Stores', badgeKey: 'stores' as const },
  { id: 'staff' as const, title: '👥 Staff', badgeKey: 'staff' as const },
  { id: 'devices' as const, title: '📱 Devices', badgeKey: 'devices' as const },

  // --- CHECKOUT CONFIGURATION ---
  { id: 'payments' as const, title: '💳 Payments', badgeKey: 'payments' as const },
  { id: 'orders' as const, title: '📦 Orders', badgeKey: 'orders' as const },
  { id: 'products' as const, title: '🏷️ Products', badgeKey: null },
  { id: 'tax' as const, title: '🧾 Tax', badgeKey: null },

  // --- MARKETING ---
  { id: 'promotions' as const, title: '🎁 Promotions', badgeKey: null },
  { id: 'pricelists' as const, title: '💰 Price Lists', badgeKey: null },
  { id: 'giftcards' as const, title: '🎫 Gift Cards', badgeKey: null },

  // --- SETTINGS ---
  { id: 'receipts' as const, title: '🖨️ Receipts', badgeKey: 'receipts' as const },
  { id: 'settings' as const, title: '⚙️ Settings', badgeKey: 'settings' as const },

  // --- BUSINESS TIER ---
  { id: 'auditlogs' as const, title: '📋 Audit Logs', badgeKey: null },

  // --- ACCOUNT ---
  { id: 'billing' as const, title: '💳 Billing', badgeKey: null },
];

export default function App() {
  const { session, isLoading, error } = useSession();
  const { badges } = useAdminBadges();
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');

  // Create tabs with badge counts
  const tabs = useMemo(() => {
    return baseTabs.map(tab => {
      if (!tab.badgeKey) {
        return { id: tab.id, title: tab.title };
      }
      const count = badges[tab.badgeKey];
      if (count > 0) {
        return { id: tab.id, title: `${tab.title} (${count})` };
      }
      return { id: tab.id, title: tab.title };
    });
  }, [badges]);

  if (isLoading) {
    return (
      <Box padding="xxxLarge">
        <Flex justifyContent="center" alignItems="center" flexDirection="column">
          <ProgressCircle size="large" />
          <Text marginTop="medium">Loading POS Admin...</Text>
        </Flex>
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding="xxxLarge">
        <Message
          type="error"
          header="Session Error"
          messages={[{ text: error }]}
        />
      </Box>
    );
  }

  if (!session) {
    return (
      <Box padding="xxxLarge">
        <Message
          type="warning"
          header="Not Authenticated"
          messages={[{ text: 'Please open this app from your BigCommerce admin panel.' }]}
        />
      </Box>
    );
  }

  return (
    <Box padding="medium">
      {/* Header */}
      <Flex justifyContent="space-between" alignItems="center" marginBottom="large">
        <FlexItem>
          <Flex alignItems="center">
            {/* Kynetik Logo */}
            <svg
              width="36"
              height="36"
              viewBox="0 0 64 64"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: '12px' }}
            >
              <circle cx="32" cy="32" r="29" stroke="#1e2444" strokeWidth="2.5" fill="none"/>
              <line x1="12" y1="22" x2="20" y2="22" stroke="#1e2444" strokeWidth="2" strokeLinecap="round"/>
              <line x1="10" y1="28" x2="20" y2="28" stroke="#1e2444" strokeWidth="2" strokeLinecap="round"/>
              <line x1="12" y1="34" x2="20" y2="34" stroke="#1e2444" strokeWidth="2" strokeLinecap="round"/>
              <line x1="10" y1="40" x2="20" y2="40" stroke="#1e2444" strokeWidth="2" strokeLinecap="round"/>
              <path d="M26 16 L26 48" stroke="#4F46E5" strokeWidth="4" strokeLinecap="round"/>
              <path d="M26 32 L44 16" stroke="#4F46E5" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M26 32 L44 48" stroke="#4F46E5" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M44 16 L44 24" stroke="#4F46E5" strokeWidth="4" strokeLinecap="round"/>
              <path d="M44 48 L44 40" stroke="#4F46E5" strokeWidth="4" strokeLinecap="round"/>
            </svg>
            <H1 marginBottom="none">Kynetik POS</H1>
          </Flex>
        </FlexItem>
        <FlexItem>
          <Flex alignItems="center">
            <Badge variant="secondary" marginRight="small" label={`Store: ${session.storeHash}`} />
            <Badge variant="success" label={`Channel: ${session.channelName} (#${session.channelId})`} />
          </Flex>
        </FlexItem>
      </Flex>

      {/* Tabs */}
      <Tabs
        activeTab={activeTab}
        items={tabs}
        onTabClick={(tabId) => setActiveTab(tabId as TabId)}
      />

      {/* Tab Content */}
      <Panel>
        <Suspense fallback={<PageLoader />}>
          {/* Operations */}
          {activeTab === 'dashboard' && <DashboardPage session={session} onNavigate={(tabId) => setActiveTab(tabId as TabId)} />}
          {activeTab === 'reports' && <ReportsPage session={session} />}
          {activeTab === 'inventory' && <InventoryPage />}

          {/* Store Setup */}
          {activeTab === 'stores' && <LocationsPage />}
          {activeTab === 'staff' && <StaffPage />}
          {activeTab === 'devices' && <DevicesPage />}

          {/* Checkout Configuration */}
          {activeTab === 'payments' && <PaymentsPage />}
          {activeTab === 'orders' && <OrdersPage session={session} />}
          {activeTab === 'products' && <ProductsPage />}
          {activeTab === 'tax' && <TaxPage />}

          {/* Marketing */}
          {activeTab === 'promotions' && <PromotionsPage session={session} />}
          {activeTab === 'pricelists' && <PriceListsPage />}
          {activeTab === 'giftcards' && <GiftCardsPage session={session} />}

          {/* Settings */}
          {activeTab === 'receipts' && <ReceiptsPage />}
          {activeTab === 'settings' && <SettingsPage session={session} />}

          {/* Business Tier */}
          {activeTab === 'auditlogs' && <AuditLogsPage />}

          {/* Account */}
          {activeTab === 'billing' && <BillingPage />}
        </Suspense>
      </Panel>
    </Box>
  );
}
